import { BenefitItemVariant, InsurancePlan, InsurancePlanBenefitItem, InsurancePlanType } from '@typesApp/InsurancePlan'
import {
  checkBenefitServiceHasCopay,
  getAdditionalPurchaseActiveBenefit,
  getBenefitServiceDescriptionInfo,
  getRoutineActiveBenefit,
  isBenefitStatusNotAvailableOrLimitReached,
} from './benefitPlanUtil'

type BaseDetails = {
  isStatusAvailable?: boolean
  isComingSoon?: boolean
  isCopay?: boolean
  serviceDescription?: string
  showFallbackDescription?: boolean
}

export type BenefitDetails = BaseDetails & {
  benefitItem: InsurancePlanBenefitItem | undefined
}

const EMPTY_DETAILS: BaseDetails = {
  serviceDescription: '',
}

/**
 * @returns benefits details for the benefit plan
 */
export const getBenefitPlanDetails = (plan: InsurancePlan, variant: BenefitItemVariant): BenefitDetails => {
  return variant === 'routine' ? getRoutineBenefitDetails(plan) : getAdditionalPurchaseBenefitDetails(plan)
}

const getRoutineBenefitDetails = (plan: InsurancePlan): BenefitDetails => {
  const { planType } = plan
  const benefitItem = getRoutineActiveBenefit(plan)
  let details: BaseDetails

  switch (planType) {
    case 'FRAME':
      details = getFrameBenefitDetails(planType, benefitItem)
      break
    case 'LENSES':
      details = getLensesBenefitDetails(planType, benefitItem)
      break
    case 'EYE_EXAM':
      details = getEyeExamBenefitDetails(planType, benefitItem)
      break
    case 'CONTACT_LENSES':
    default:
      details = getGenericBenefitDetails(planType, benefitItem)
      break
  }

  return {
    ...details,
    benefitItem,
  }
}

const getAdditionalPurchaseBenefitDetails = (plan: InsurancePlan): BenefitDetails => {
  const { planType } = plan
  const benefitItem = getAdditionalPurchaseActiveBenefit(plan)
  let details = EMPTY_DETAILS

  switch (planType) {
    case 'FRAME':
    case 'LENSES':
      details = getFrameWithLensesBenefitDetails()
      break
  }

  return {
    ...details,
    benefitItem,
  }
}

const getFrameBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isStatusAvailable, serviceDescription, showFrameFallbackDescription } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (isBenefitStatusNotAvailableOrLimitReached(benefitItem)) {
    return {
      isStatusAvailable,
    }
  }

  if (serviceDescription && !showFrameFallbackDescription) {
    return { serviceDescription, isStatusAvailable }
  }

  return {
    isStatusAvailable,
    showFallbackDescription: isStatusAvailable,
  }
}

const getLensesBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isStatusAvailable, serviceDescription, showLensesFallbackDescription } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (isBenefitStatusNotAvailableOrLimitReached(benefitItem)) {
    return {
      isStatusAvailable,
    }
  }

  if (serviceDescription && !showLensesFallbackDescription) {
    return { serviceDescription, isStatusAvailable }
  }

  return {
    isStatusAvailable,
    showFallbackDescription: isStatusAvailable,
  }
}

const getEyeExamBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isStatusAvailable, serviceDescription, benefitService } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (isBenefitStatusNotAvailableOrLimitReached(benefitItem)) {
    return {
      isStatusAvailable,
    }
  }

  const isCopay = checkBenefitServiceHasCopay(benefitService)

  return {
    isCopay,
    isStatusAvailable,
    serviceDescription,
  }
}

const getGenericBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isComingSoon, isStatusAvailable, serviceDescription } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (isComingSoon) {
    return {
      isComingSoon,
    }
  }

  if (isBenefitStatusNotAvailableOrLimitReached(benefitItem)) {
    return {
      isStatusAvailable,
    }
  }

  return {
    isStatusAvailable,
    serviceDescription,
  }
}

const getFrameWithLensesBenefitDetails = (): BaseDetails => {
  return {
    isStatusAvailable: true,
  }
}
